import React, { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import * as signalR from '@microsoft/signalr';
import {
  ConversationLogEvent,
  ConversationReducerProvider,
  ConversationState,
  MessageHistoryItem,
} from '@rbx/conv-ai-provider';
import { createConversation, CreateConversationResponse } from '@modules/clients/convAi';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import { signalrConnectionUrl } from '@modules/utilities/constants/baseUrl';
import { useAssistantOptIn } from './AssistantOptInProvider';

const signalRLogLevel =
  process.env.targetEnvironment === 'production'
    ? signalR.LogLevel.Warning
    : signalR.LogLevel.Trace;

const conversationNamespace = 'MachineLearningChat';

const convertToRecordString = (params?: Record<string, unknown>): Record<string, string> => {
  if (!params) return {};

  return Object.entries(params).reduce(
    (acc, [key, value]) => {
      if (value === null || value === undefined) {
        acc[key] = '';
      } else if (typeof value === 'object') {
        acc[key] = JSON.stringify(value);
      } else {
        acc[key] = String(value);
      }
      return acc;
    },
    {} as Record<string, string>,
  );
};

const ConversationLogEventToDocsAssistantEvent: Record<ConversationLogEvent, DocsAssistantEvent> = {
  [ConversationLogEvent.BotStartStreamingResponse]: DocsAssistantEvent.BotStartedStreamingResponse,
  [ConversationLogEvent.BotFinishStreamingResponse]:
    DocsAssistantEvent.BotFinishedStreamingResponse,
  [ConversationLogEvent.BotErrorStreamingResponse]: DocsAssistantEvent.BotErroredStreamingResponse,
  [ConversationLogEvent.ConnectionReconnecting]: DocsAssistantEvent.ConnectionReconnecting,
  [ConversationLogEvent.ConnectionReconnected]: DocsAssistantEvent.ConnectionReconnected,
  [ConversationLogEvent.ConnectionClosed]: DocsAssistantEvent.ConnectionClosed,
  [ConversationLogEvent.UserSubmitPrompt]: DocsAssistantEvent.UserSubmittedPrompt,
  [ConversationLogEvent.UserSubmitRegenerationPrompt]:
    DocsAssistantEvent.UserClickedMessageRegenerateButton,
  [ConversationLogEvent.UserClickStop]: DocsAssistantEvent.UserClickedStopButton,
  [ConversationLogEvent.UserRateResponse]: DocsAssistantEvent.UserRatedBotResponse,
  [ConversationLogEvent.UserStartsRegenerateMessage]:
    DocsAssistantEvent.UserClickedMessageRegenerateButton,
  [ConversationLogEvent.UserSubmitPromptResponseError]:
    DocsAssistantEvent.CreateConversationResponseError,
};

const DocsAssistantContextProvider: FC<PropsWithChildren<object>> = ({ children }) => {
  const logConversationEvent = useCallback(
    (
      eventName: ConversationLogEvent,
      state: ConversationState,
      extraParams?: Record<string, unknown>,
    ) => {
      const docsAssistantEventName = ConversationLogEventToDocsAssistantEvent[eventName];
      switch (eventName) {
        // Handled in handleDispatchCreateConversationError as a CreateConversationResponseError
        case ConversationLogEvent.UserSubmitPromptResponseError:
        case ConversationLogEvent.BotFinishStreamingResponse:
        case ConversationLogEvent.BotErrorStreamingResponse:
        case ConversationLogEvent.ConnectionReconnecting:
        case ConversationLogEvent.ConnectionReconnected:
        case ConversationLogEvent.ConnectionClosed:
        case ConversationLogEvent.BotStartStreamingResponse:
          unifiedLoggerClient.logImpressionEvent({
            eventName: docsAssistantEventName,
            parameters: {
              messageId: state.activeRequestId ?? '',
              conversationId: state.conversationId ?? '',
              ...convertToRecordString(extraParams),
            },
          });
          break;
        case ConversationLogEvent.UserSubmitPrompt:
        case ConversationLogEvent.UserSubmitRegenerationPrompt:
        case ConversationLogEvent.UserClickStop:
        case ConversationLogEvent.UserRateResponse:
        case ConversationLogEvent.UserStartsRegenerateMessage:
          unifiedLoggerClient.logClickEvent({
            eventName: docsAssistantEventName,
            parameters: {
              messageId: state.activeRequestId ?? '',
              conversationId: state.conversationId ?? '',
              ...convertToRecordString(extraParams),
            },
          });
          break;
        default: {
          const exhaustiveCheck: never = eventName;
          throw new Error(`Unhandled conversation log event: ${exhaustiveCheck}`);
        }
      }
    },
    [],
  );
  const promptAssistantResponse = useCallback(
    async (state: ConversationState): Promise<CreateConversationResponse> => {
      if (!state.activePromptMessage) throw new Error('No active prompt message');
      return createConversation({
        conversationId: state.conversationId,
        messageHistory: state.messageHistory as MessageHistoryItem[],
        messageToSend: state.activePromptMessage,
      });
    },
    [],
  );
  const assistantOptIn = useAssistantOptIn();

  const conversationProviderProps = useMemo(
    () => ({
      convAiConfig: {
        assistantOptIn,
        conversationNamespace,
        promptAssistantResponse,
        logConversationEvent,
      },
      signalRConfig: {
        signalRConnectionUrl: signalrConnectionUrl,
        signalRLogLevel,
      },
    }),
    [logConversationEvent, promptAssistantResponse, assistantOptIn],
  );

  return (
    <ConversationReducerProvider {...conversationProviderProps}>
      {children}
    </ConversationReducerProvider>
  );
};
export default DocsAssistantContextProvider;
